import { useCallback, useEffect, useRef, useState } from 'react'
import * as logger from 'loglevel'
import {
  publishModalActionTimeout,
  shouldDismissPromptOnReset,
  shouldExitPublishActionTimeout,
} from '../../Utils/ActionTimeout'

export function useActionTimeout({
  frameContentRef,
  html,
  anearParticipantRef,
  lastIdRef,
  anearParticipant,
  eventClickHandlers,
}) {
  const [actionTimeoutState, setActionTimeoutState] = useState({
    open: false,
    timeoutId: null,
    prompt: null,
    resetLabel: 'Yes',
    exitLabel: 'No',
    modalTimeoutMs: 0,
    timeoutEl: null,
  })
  const modalPenaltyTimerRef = useRef(null)
  const modalPenaltyContextRef = useRef({ timeoutEl: null, timeoutId: null, modalMs: 0 })
  const actionTimeoutElRef = useRef(null)

  useEffect(() => {
    actionTimeoutElRef.current = actionTimeoutState.timeoutEl
  }, [actionTimeoutState.timeoutEl])

  const clearModalPenaltyTimer = useCallback(() => {
    if (modalPenaltyTimerRef.current) {
      clearTimeout(modalPenaltyTimerRef.current)
      modalPenaltyTimerRef.current = null
    }
    modalPenaltyContextRef.current = { timeoutEl: null, timeoutId: null, modalMs: 0 }
  }, [])

  const publishModalPenaltyIfNeeded = useCallback(() => {
    const { timeoutEl, timeoutId, modalMs } = modalPenaltyContextRef.current
    if (!timeoutEl) return
    if (timeoutEl.getAttribute('data-anear-timeout-published') === 'modal_timeout') {
      clearModalPenaltyTimer()
      setActionTimeoutState(prev => ({ ...prev, open: false, timeoutEl: null, timeoutId: null }))
      return
    }

    const participant = anearParticipantRef.current
    if (participant) {
      logger.info('[ABR] publishing ACTION_TIMEOUT (modal expiry)')
      publishModalActionTimeout({
        timeoutEl,
        publishAction: (msg) => participant.publishAction(msg),
        renderId: lastIdRef.current,
        modalMs: modalMs || null,
        timeoutId: timeoutId || null,
      })
    }

    clearModalPenaltyTimer()
    setActionTimeoutState(prev => ({ ...prev, open: false, timeoutEl: null, timeoutId: null }))
  }, [clearModalPenaltyTimer, anearParticipantRef, lastIdRef])

  const scheduleModalPenaltyTimer = useCallback((timeoutEl, modalMs, timeoutId) => {
    clearModalPenaltyTimer()
    if (!timeoutEl || !modalMs || modalMs <= 0) return
    modalPenaltyContextRef.current = { timeoutEl, timeoutId, modalMs }
    modalPenaltyTimerRef.current = setTimeout(() => {
      publishModalPenaltyIfNeeded()
    }, modalMs)
  }, [clearModalPenaltyTimer, publishModalPenaltyIfNeeded])

  const disarmTimeoutWrapper = useCallback((timeoutEl) => {
    clearModalPenaltyTimer()
    timeoutEl?.disarmForUserAction?.()
  }, [clearModalPenaltyTimer])

  useEffect(() => () => clearModalPenaltyTimer(), [clearModalPenaltyTimer])

  // Listen for timeout expiry events from <anear-timeout> and per-action duration timers.
  useEffect(() => {
    const root = frameContentRef.current
    if (!root) return undefined

    const onTimeoutExpired = (e) => {
      const detail = e?.detail || {}
      const timeoutEl = detail.timeoutEl || null
      if (!timeoutEl) return

      // No prompt: only wait first duration; on expiry send ACTION_TIMEOUT on actions channel.
      if (detail.sendActionTimeout) {
        if (timeoutEl.getAttribute('data-anear-timeout-published') === 'action_timeout') {
          return
        }
        const participant = anearParticipantRef.current
        if (participant) {
          const payload = {
            ACTION_TIMEOUT: {
              stage: 'action_timeout',
              renderId: lastIdRef.current,
              actionMs: detail.actionMs ?? null,
              timeoutId: detail.timeoutId ?? null,
            },
          }
          logger.info('[ABR] publishing ACTION_TIMEOUT (no prompt)', payload)
          timeoutEl.setAttribute('data-anear-timeout-published', 'action_timeout')
          participant.publishAction(JSON.stringify(payload))
        }
        return
      }

      setActionTimeoutState({
        open: true,
        timeoutId: detail.timeoutId || null,
        prompt: detail.prompt || 'Do you still want to participate?',
        resetLabel: detail.resetLabel || 'Yes',
        exitLabel: detail.exitLabel ?? null,
        modalTimeoutMs: Number(detail.modalMs) || 0,
        timeoutEl,
      })
      scheduleModalPenaltyTimer(timeoutEl, Number(detail.modalMs) || 0, detail.timeoutId || null)
    }

    root.addEventListener('anear-timeout-expired', onTimeoutExpired)
    return () => root.removeEventListener('anear-timeout-expired', onTimeoutExpired)
  }, [html, scheduleModalPenaltyTimer, frameContentRef, anearParticipantRef, lastIdRef])

  const handleTimeoutReset = useCallback(() => {
    const timeoutEl = actionTimeoutElRef.current
    if (!timeoutEl) return

    if (shouldDismissPromptOnReset(timeoutEl)) {
      timeoutEl.dismissPrompt?.()
      setActionTimeoutState(prev => ({ ...prev, open: false }))
      return
    }

    clearModalPenaltyTimer()
    timeoutEl.resetFromYes?.()
    setActionTimeoutState(prev => ({ ...prev, open: false, timeoutEl: null, timeoutId: null }))
  }, [clearModalPenaltyTimer])

  const handleTimeoutExit = useCallback(async () => {
    const { timeoutEl, modalTimeoutMs, timeoutId } = actionTimeoutState

    clearModalPenaltyTimer()
    setActionTimeoutState(prev => ({ ...prev, open: false, timeoutEl: null, timeoutId: null }))

    if (shouldExitPublishActionTimeout(timeoutEl) && anearParticipant) {
      logger.info('[ABR] publishing ACTION_TIMEOUT (exit forfeit)')
      publishModalActionTimeout({
        timeoutEl,
        publishAction: (msg) => anearParticipant.publishAction(msg),
        renderId: lastIdRef.current,
        modalMs: modalTimeoutMs || null,
        timeoutId: timeoutId || null,
      })
      return
    }

    // Default: exit button leaves the event (permanent), like nav exit, with no further confirmation.
    try {
      await eventClickHandlers?.(null, 'exit', { noPrompt: true })
    } catch (err) {
      logger.error('ActionTimeout: exit failed', err)
    }
  }, [actionTimeoutState, anearParticipant, eventClickHandlers, clearModalPenaltyTimer, lastIdRef])

  return {
    actionTimeoutState,
    disarmTimeoutWrapper,
    handleTimeoutReset,
    handleTimeoutExit,
  }
}
